import PropTypes from 'prop-types'
import React from 'react'
import get from 'lodash-es/get'
import last from 'lodash-es/last'
import * as titleize from 'titleize'

import FilterCategoryWithBreadcrumbs from './filterCategoryWithBreadcrumbs'
import FiltersCategory from './filtersCategory'
import FiltersTags from './filtersTags'
import FiltersPrice from './filtersPrice'
import FiltersCompatibleWith from './filtersCompatibleWith'
import FiltersSoftwareVersion from './filtersSoftwareVersion'
import FiltersPolygons from './filtersPolygons'
import FiltersFileType from './filtersFileType'
import SelectedFilters from './selectedFilters'
import FiltersSize from './filtersSize'
import FiltersColors from './filtersColors'
import FiltersLength from './filtersLength'
import FiltersVocals from './filtersVocals'
import FiltersDropdown from './filtersDropdown'
import FiltersBoolean from './filtersBoolean'
import FiltersProMusic from './filtersProMusic'

const withOption = (options, option) => {
  return options && option ? [option, ...options] : []
}

// TODO: Jonathan WIlliams - Remove this once caches are cleared
// Once caches of th search results are refreshed option.name will resolve to a SEO friendly category name
// until then we still need to do this titleize magic here
const withCategoryLabels = options => {
  if (!options) return []
  return options.map(option => {
    const pathSegments = option.key.split('/')
    const slug = last(pathSegments) || ''
    const label = option.name || titleize(slug.replace('-', ' '))

    return {
      ...option,
      label
    }
  })
}

const SearchFilters = ({
  aggregations,
  currentSite,
  locationParams,
  makeSearchLink
}) => {
  const basicFilters = [
    {
      Component: FilterCategoryWithBreadcrumbs,
      nonJS: true,
      isActive:
        !!get(aggregations, 'category.length') && currentSite != 'themeforest',
      props: {
        items: withOption(withCategoryLabels(aggregations.category), {
          description: null,
          key: '',
          label: 'All categories',
          count: get(aggregations, 'category_root_count.count')
        })
      }
    },
    {
      Component: FiltersCategory,
      isActive:
        !!get(aggregations, 'category.length') && currentSite == 'themeforest',
      props: {
        categoryRootCount: get(aggregations, 'category_root_count.count'),
        items: aggregations.category
      }
    },
    {
      Component: FiltersTags,
      isActive: !!get(aggregations, 'tags.length'),
      props: {
        items: aggregations.tags
      }
    },
    {
      Component: FiltersPrice,
      isActive: true,
      props: {
        cost: aggregations.cost
      }
    },
    {
      Component: FiltersDropdown,
      isActive: true,
      props: {
        name: 'sales',
        label: 'Sales',
        ignoreOptions: ['varies'],
        items: aggregations.sales_count
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'rating.length') > 0,
      props: {
        name: 'rating_min',
        label: 'Rating',
        prependShowAllOption: false,
        items: aggregations.rating
      }
    }
  ]

  const extraFilters = [
    {
      Component: FiltersBoolean,
      isActive: get(aggregations, 'hosting_setup.count') > 0,
      props: {
        name: 'hosting_setup',
        label: 'Hosting & Setup',
        count: get(aggregations, 'hosting_setup.count')
      }
    },
    {
      Component: FiltersSoftwareVersion,
      isActive: get(aggregations, 'platform.length') > 0,
      props: {
        items: aggregations.platform,
        platformRootCount: get(aggregations, 'platform_root_count.count')
      }
    },
    {
      Component: FiltersCompatibleWith,
      isActive: get(aggregations, 'compatible_with.length') > 0,
      props: {
        items: aggregations.compatible_with
      }
    },
    {
      Component: FiltersPolygons,
      isActive: aggregations.poly_count,
      props: {
        items: aggregations.poly_count
      }
    },
    {
      Component: FiltersFileType,
      isActive: get(aggregations, 'file_formats.length') > 0,
      props: {
        items: aggregations.file_formats
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'orientation.length') > 0,
      props: {
        name: 'orientation',
        label: 'Orientation',
        items: aggregations.orientation
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'resolution.length') > 0,
      props: {
        name: 'resolution_min',
        label: 'Minimum resolution',
        items: aggregations.resolution
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'frame_rate.length') > 0,
      props: {
        name: 'frame_rate',
        label: 'Frame rate',
        items: aggregations.frame_rate
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'requires_plugins.length') > 0,
      props: {
        name: 'requires_plugins',
        label: 'Requires Plugins',
        items: aggregations.requires_plugins
      }
    },
    {
      Component: FiltersSize,
      isActive: get(aggregations, 'size.length') > 0,
      props: {
        items: aggregations.size
      }
    },
    {
      Component: FiltersColors,
      isActive: get(aggregations, 'colors.length') > 0,
      props: {
        items: aggregations.colors
      }
    },
    {
      Component: FiltersLength,
      isActive: !!aggregations.length,
      props: {
        length: aggregations.length
      }
    },
    {
      Component: FiltersVocals,
      isActive: get(aggregations, 'vocals_in_audio.length') > 0,
      props: {
        items: aggregations.vocals_in_audio
      }
    },
    {
      Component: FiltersDropdown,
      isActive: get(aggregations, 'tempo.length') > 0,
      props: {
        name: 'tempo',
        label: 'Tempo',
        ignoreOptions: ['varies'],
        items: aggregations.tempo
      }
    },
    {
      Component: FiltersBoolean,
      isActive: !!aggregations.alpha,
      props: {
        name: 'alpha',
        label: 'Alpha channel',
        count: get(aggregations, 'alpha.count')
      }
    },
    {
      Component: FiltersBoolean,
      isActive: !!aggregations.looped,
      props: {
        name: 'looped',
        label: 'Looped',
        count: get(aggregations, 'looped.count')
      }
    },
    {
      Component: FiltersProMusic,
      isActive: currentSite === 'audiojungle'
    }
  ]

  const activeBasicFilters = basicFilters.filter(filter => filter.isActive)
  const activeExtraFilters = extraFilters.filter(filter => filter.isActive)

  return (
    <div
      className="search-facet-panel"
      data-test-selector="search-filters-default"
    >
      <SelectedFilters
        locationParams={locationParams}
        currentSite={currentSite}
      />
      <div className="search-facet">
        {activeBasicFilters.map((filter, index, list) => {
          return (
            <div
              key={index}
              className={filter.nonJS ? '' : 'fds-u-is-hidden--no-js'}
            >
              <filter.Component
                key={index}
                locationParams={locationParams}
                makeSearchLink={makeSearchLink}
                isAtTop={index === 0}
                isAtBottom={list.length === index + 1}
                {...filter.props}
              />
            </div>
          )
        })}
      </div>

      {!!activeExtraFilters.length && (
        <div className="search-facet">
          {activeExtraFilters.map((filter, index, list) => {
            return (
              <div
                key={index}
                className={filter.nonJS ? '' : 'fds-u-is-hidden--no-js'}
              >
                <filter.Component
                  key={index}
                  locationParams={locationParams}
                  makeSearchLink={makeSearchLink}
                  isAtTop={index === 0}
                  isAtBottom={list.length === index + 1}
                  {...filter.props}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

SearchFilters.propTypes = {
  aggregations: PropTypes.object,
  locationParams: PropTypes.object.isRequired,
  currentSite: PropTypes.string,
  makeSearchLink: PropTypes.func.isRequired
}

SearchFilters.defaultProps = {
  aggregations: {}
}

export default SearchFilters
